import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FiHome, FiMessageSquare, FiList, FiGrid, FiRefreshCw, FiCalendar } from 'react-icons/fi';
import { Toaster } from 'sonner';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Keyboard } from '@capacitor/keyboard';
import { isNativePlatform } from '../../utils/nativeUtils';
import { useWorkspace } from '../../context/WorkspaceContext';

const MobileLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userRole } = useWorkspace();
    const isNative = isNativePlatform();
    const scrollContainerRef = useRef(null);
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const pullStartY = useRef(0);
    const PULL_THRESHOLD = 120; // Pixels to pull to trigger refresh

    // Mapping of paths to highlight correct tab
    const getActiveTab = () => {
        const path = location.pathname;
        if (path.includes('/mobile/chat')) return 'chat';
        if (path.includes('/mobile/calendar')) return 'calendar';
        if (path.includes('/mobile/tasks')) return 'tasks';
        if (path.includes('/mobile/actions')) return 'actions';
        return 'home'; // default
    };

    const activeTab = getActiveTab();

    const tabs = [
        { id: 'home', label: 'Home', icon: FiHome, path: '/mobile' },
        { id: 'calendar', label: 'Calendar', icon: FiCalendar, path: '/mobile/calendar' },
        ...(userRole === 'super_admin' ? [{ id: 'chat', label: 'Chat', icon: FiMessageSquare, path: '/mobile/chat' }] : []),
        { id: 'tasks', label: 'Tasks', icon: FiList, path: '/mobile/tasks' },
        { id: 'actions', label: 'Actions', icon: FiGrid, path: '/mobile/actions' },
    ];

    useEffect(() => {
        if (isNative) {
            // Handle Keyboard resize events to avoid occlusion
            Keyboard.addListener('keyboardWillShow', info => {
                document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
            });
            Keyboard.addListener('keyboardWillHide', () => {
                document.documentElement.style.setProperty('--keyboard-height', '0px');
            });

            return () => {
                Keyboard.removeAllListeners();
            };
        }
    }, [isNative]);

    // Pull to Refresh Logic
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleTouchStart = (e) => {
            if (container.scrollTop <= 0) {
                pullStartY.current = e.touches[0].clientY;
            }
        };

        const handleTouchMove = (e) => {
            if (isRefreshing) return;
            const y = e.touches[0].clientY;
            const diff = y - pullStartY.current;

            if (container.scrollTop <= 0 && diff > 0) {
                // Resistance effect
                setPullDistance(Math.min(diff * 0.5, PULL_THRESHOLD + 20));
            } else {
                setPullDistance(0);
            }
        };

        const handleTouchEnd = async () => {
            if (isRefreshing) return;

            if (pullDistance > PULL_THRESHOLD) {
                setIsRefreshing(true);
                setPullDistance(60); // Snap to loading position

                if (isNative) {
                    await Haptics.impact({ style: ImpactStyle.Medium });
                }

                // Trigger Reload
                setTimeout(() => {
                    window.location.reload();
                }, 800); // Visual delay
            } else {
                setPullDistance(0);
            }
        };

        container.addEventListener('touchstart', handleTouchStart, { passive: true });
        container.addEventListener('touchmove', handleTouchMove, { passive: false }); // non-passive to prevent scroll chaining if needed
        container.addEventListener('touchend', handleTouchEnd);

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
        };
    }, [pullDistance, isRefreshing, isNative]);


    const handleTabClick = async (path) => {
        if (isNative) {
            await Haptics.impact({ style: ImpactStyle.Light });
        }
        navigate(path);
    };

    return (
        <div className="flex flex-col h-[100dvh] w-full bg-charcoal-50 dark:bg-black text-charcoal-900 dark:text-charcoal-200 overflow-hidden relative transition-colors duration-200">
            <Toaster position="top-center" />

            {/* Pull to Refresh Indicator */}
            <div
                className="absolute top-0 left-0 right-0 flex items-center justify-center pointer-events-none z-50 transition-all duration-200 ease-out"
                style={{
                    height: `${pullDistance}px`,
                    opacity: Math.min(pullDistance / 50, 1),
                    transform: `translateY(${Math.min(pullDistance/2, 20)}px)`
                }}
            >
                <div className="bg-white dark:bg-charcoal-800 rounded-full p-2 shadow-md border border-charcoal-200 dark:border-charcoal-700">
                    <FiRefreshCw
                        className={`text-charcoal-600 dark:text-charcoal-300 w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`}
                        style={{ transform: `rotate(${pullDistance * 2}deg)` }}
                    />
                </div>
            </div>

            {/* Main Content Area */}
            {/* Added padding bottom to account for tab bar + safe area */}
            {/* Adjusted padding to ensure content is not hidden behind home indicator or tab bar */}
            {/* Use 100dvh to respect dynamic viewport height on mobile browsers */}
            {/* Capacitor natively resizes the webview when keyboard opens, so manually adding keyboard height double-shifts content */}
            <main
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto transition-[padding] duration-200 pt-[env(safe-area-inset-top)] overscroll-contain"
                style={{
                    paddingBottom: 'calc(90px + env(safe-area-inset-bottom))',
                    transform: isRefreshing ? `translateY(60px)` : `translateY(0px)`,
                    transition: isRefreshing ? 'transform 0.2s ease-out' : 'transform 0.2s ease-out'
                }}
            >
                <Outlet />
            </main>

            {/* Bottom Navigation Bar */}
            <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-black border-t border-charcoal-200 dark:border-charcoal-800 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] dark:shadow-[0_-2px_10px_rgba(0,0,0,0.2)] z-50 pb-[env(safe-area-inset-bottom)] transition-colors duration-200">
                <div className="flex items-center justify-around h-[60px]">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabClick(tab.path)}
                                className={`flex flex-col items-center justify-center w-full h-full space-y-1 transition-colors ${
                                    isActive ? 'text-blue-600 dark:text-blue-500' : 'text-charcoal-400 hover:text-charcoal-600 dark:text-charcoal-500 dark:hover:text-charcoal-300'
                                }`}
                            >
                                <Icon size={24} strokeWidth={isActive ? 2.5 : 2} />
                                <span className="text-[10px] font-medium">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </nav>
        </div>
    );
};

export default MobileLayout;
